/* Random melody generator shared by the guitar and vocal exercises. */
import { mod, type ScaleName, scaleNotes } from '../../core/theory';
import { choice, clamp, type Rng } from '../../core/util';

export interface MelodyOptions {
  tonic: number;
  scale: ScaleName;
  len: number;
  lo: number;
  hi: number;
  maxLeap?: number;
  /** probability of a step (vs. a leap) */
  stepBias?: number;
  allowRepeat?: boolean;
  endOnTonic?: boolean;
  /** insert one chromatic approach note */
  chromatic?: boolean;
}

export function melody(rng: Rng, o: MelodyOptions): number[] {
  const notes = scaleNotes(o.tonic, o.scale, o.lo, o.hi);
  if (!notes.length) return [];
  const minor = /min|blues/i.test(o.scale);
  const chordPcs = [0, minor ? 3 : 4, 7].map((i) => mod(o.tonic + i, 12));
  const mid = (o.lo + o.hi) / 2;
  const cands = notes.map((m, i) => ({ m, i })).filter((x) => chordPcs.includes(mod(x.m, 12)));
  cands.sort((a, b) => Math.abs(a.m - mid) - Math.abs(b.m - mid));
  let idx = (
    cands.length ? choice(cands.slice(0, Math.min(4, cands.length)), rng) : { i: Math.floor(notes.length / 2) }
  ).i;
  const out = [notes[idx] as number];
  const maxLeap = o.maxLeap || 7;
  for (let k = 1; k < o.len; k++) {
    let nidx = idx;
    let tries = 0;
    do {
      const r = rng();
      let step = r < (o.stepBias == null ? 0.6 : o.stepBias) ? 1 : r < 0.86 ? 2 : r < 0.96 ? 3 : 4;
      if (o.allowRepeat && rng() < 0.1) step = 0;
      let dir = rng() < 0.5 ? -1 : 1;
      if ((notes[idx] as number) > mid + 4 && rng() < 0.7) dir = -1;
      if ((notes[idx] as number) < mid - 4 && rng() < 0.7) dir = 1;
      nidx = idx + dir * step;
      tries++;
    } while (
      (nidx < 0 ||
        nidx >= notes.length ||
        Math.abs((notes[nidx] as number) - (notes[idx] as number)) > maxLeap ||
        (nidx === idx && !o.allowRepeat)) &&
      tries < 40
    );
    idx = clamp(nidx, 0, notes.length - 1);
    out.push(notes[idx] as number);
  }
  if (o.endOnTonic && o.len >= 3) {
    const prev = out[out.length - 2] as number;
    const tonics = notes.filter((m) => mod(m, 12) === mod(o.tonic, 12) && Math.abs(m - prev) <= maxLeap && m !== prev);
    if (tonics.length) {
      tonics.sort((a, b) => Math.abs(a - prev) - Math.abs(b - prev));
      out[out.length - 1] = tonics[0] as number;
    }
  }
  if (o.chromatic && o.len >= 3) {
    const k = 1 + Math.floor(rng() * (o.len - 2));
    const tgt = out[k + 1] as number;
    const cand = tgt + (rng() < 0.5 ? -1 : 1);
    if (!notes.includes(cand) && cand >= o.lo && cand <= o.hi && cand !== out[k - 1]) out[k] = cand;
  }
  return out;
}

/** "↑ adım · ↓ atlama · → aynı" */
export function contour(ms: readonly number[]): string {
  const parts: string[] = [];
  for (let i = 1; i < ms.length; i++) {
    const d = (ms[i] as number) - (ms[i - 1] as number);
    parts.push(d === 0 ? '→ aynı' : `${d > 0 ? '↑ ' : '↓ '}${Math.abs(d) <= 2 ? 'adım' : 'atlama'}`);
  }
  return parts.join(' · ');
}
